import { motion } from "framer-motion";
import styled from "styled-components";
import { colors } from "utils";
import { CloseButton } from "../CloseButton/CloseButton";

// The following function is needed as "text-overflow: ellipsis" works only with "white-space: no-wrap" in CSS.
// Adding the latter results in Oko Assistant phrase being limited to 1 line.
function trimPhrase(str, customMaxLength) {
  const maxLength = customMaxLength || 230;
  return str.length > maxLength ? str.substring(0, maxLength - 3) + "..." : str.substring(0, maxLength);
}

// Parse **text** markdown syntax and render as bold
function parseMarkdownBold(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

export const SpeechBubble = ({
  setDisplay,
  text,
  minWidth = 300,
  positionLeft = 132,
  positionBottom = 60,
  hide = false,
  isCustom = false,
  arrowDirection = "left",
  maxLength = 230,
  role,
  ariaLabel,
  // Forwarded so callers can extend the bubble via styled(SpeechBubble).
  className,
}) => {
  return <Bubble
    className={className}
    role={role}
    aria-label={ariaLabel}
    minWidth={minWidth}
    positionBottom={positionBottom}
    positionLeft={positionLeft}
    hide={hide}
    isCustom={isCustom}
    $arrowDirection={arrowDirection}
  >
    <TextContainer
      transition={{
        delay: 0.5,
      }}
      animate={{ width: "100%", whiteSpace: "normal" }}
    >
      {/* The close button box reaches 40px in from the bubble edge; the extra
          padding keeps the text clear of it, and overflow-wrap stops a single
          long token (a tag, a URL) from running under it. */}
      <div style={{ fontSize: "16px", fontWeight: 500, lineHeight: "normal", overflowWrap: "anywhere", paddingRight: setDisplay ? "24px" : 0 }}>
        {parseMarkdownBold(trimPhrase(text, maxLength))}
      </div>
    </TextContainer>
    {
      setDisplay && <CloseButton
        onClick={setDisplay}
        color={colors.blue500}
        ariaLabel="Close text speech"
        customCss="pointer-events: auto;"
      />
    }
  </Bubble>
}

const Bubble = styled.div`
  @keyframes slideFadeIn {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  position: absolute;
  display: flex;
  opacity: ${({ hide }) => hide ? 0 : 1};
  pointer-events: none;
  transition: opacity 0.2s ease-in-out;
  justify-content: center;
  align-items: flex-end;
  padding: 24px;
  max-width: 450px;
  min-width: ${({ minWidth }) => minWidth}px;
  border-radius: 32px 32px 32px 0;
  background: ${colors.blue100};
  z-index: 10;
  left: ${({ positionLeft }) => positionLeft}px;
  bottom: ${({ positionBottom }) => positionBottom}px;

  &::before {
    content: '';
    position: absolute;
    bottom: -20px;
    left: -18px;
    width: 0;
    height: 0;
    border-top: 20px solid ${colors.blue100};
    border-right: 20px solid ${colors.blue100};
    border-bottom: 20px solid transparent;
    border-left: 20px solid transparent;
    transform: rotate(-45deg);
  }

  ${({ isCustom }) => {
    if (isCustom) {
      return `
        padding: 1rem;
        font-size: 0.75rem;
        border-radius: .5rem;
        z-index: 10;
        animation: slideFadeIn 0.6s ease-out forwards;

        &::before {
          top: 16px;
          left: -10px;
          transform: rotate(225deg);
          z-index: 0;
        }
        `
    }
  }}

  ${({ $arrowDirection }) => {
    if ($arrowDirection === "bottom") {
      return `
        border-radius: 32px;

        &::before {
          bottom: -20px;
          left: 30px;
          transform: rotate(45deg);
        }
      `
    }
  }}
`;

const TextContainer = styled(motion.div)`
  align-self: normal;
  line-height: 1;
  overflow: hidden;
  font-size: 30px;
  border-radius: 0.25rem;
  flex: 1;

  > div {
    color: ${colors.nightlyWoods500};
  }
`;

